import {
  DossierTable,
  Field,
  FieldGroup,
  FieldProse,
  Gating,
  NumberedSection,
  Section6SamdFields,
  obj,
  prose,
} from './DossierPrimitives';

/**
 * Shared IVD eSTAR section components — reused by the 510(k), De Novo, and PMA
 * result renderers (mirrors the backend `_ivd_sections`). Parametrized by the
 * dossier section key + display number so the same UI serves §6/§11 (device
 * description), §14/§15/§17 (shelf-life), §19/§22 (performance), and PMA's
 * added analytical section.
 */

export function IvdStudyRow({
  label,
  study,
}: {
  label: string;
  study: Record<string, unknown>;
}) {
  return (
    <>
      <Gating question={`${label} — study performed?`} section={study} />
      {prose(study.summary) != null && (
        <FieldProse label={`${label} — summary:`} body={prose(study.summary)} />
      )}
    </>
  );
}

export function IvdDeviceDescription({
  dossier,
  sectionKey,
  num,
}: {
  dossier: Record<string, unknown>;
  sectionKey: string;
  num: number;
}) {
  const s = obj(dossier, sectionKey);
  const ai = obj(s, 'assay_instrument_information');
  const fa = obj(s, 'failure_alerts_fail_safe');
  return (
    <NumberedSection prefix="Section" num={num} title="Device Description">
      <Field label="Device trade name / proprietary name:" value={s.trade_name} />
      <Field label="Common or usual name:" value={s.common_name} />
      <Field label="Models / sizes included in this submission:" value={s.models_included} />
      <FieldProse label="Detailed device description:" body={prose(s.detailed_description)} />
      <h4 className="text-[14px] font-bold text-navy mt-4 mb-2">Assay &amp; Instrument Information</h4>
      <Field label="Is there an instrument associated with the assay(s)?" value={ai.has_instrument} />
      <Field label="Instrument previously FDA-reviewed / under review?" value={ai.instrument_fda_reviewed} />
      <Field label="Instrument modified to run the new assay(s)?" value={ai.instrument_modified_for_assay} />
      <FieldProse label="Assay methodology & instrument/platform:" body={prose(ai.description)} />
      <h4 className="text-[14px] font-bold text-navy mt-4 mb-2">Failure Alerts &amp; Fail-Safe</h4>
      <Gating
        question="Does the device/instrument have failure alerts or fail-safe mechanisms?"
        section={fa}
      />
    </NumberedSection>
  );
}

// Lockstep with backend _ivd_sections.device_description_body_510k — the FDA
// "Format for Traditional and Abbreviated 510(k)s" §(10) STRUCTURED base +
// IVD assay/instrument + failure blocks. 510(k)-IVD ONLY (De Novo / PMA IVD
// keep IvdDeviceDescription above).
export function Ivd510kDeviceDescription({
  dossier,
  sectionKey,
  num,
}: {
  dossier: Record<string, unknown>;
  sectionKey: string;
  num: number;
}) {
  const s = obj(dossier, sectionKey);
  const ai = obj(s, 'assay_instrument_information');
  const fa = obj(s, 'failure_alerts_fail_safe');
  // Backward-compat: an old 510(k)-IVD run carrying the legacy prose description.
  const legacy = prose(s.detailed_description);
  const legacyStr = typeof legacy === 'string' ? legacy : '';
  const hasLegacy = legacyStr.trim().length > 0 && !legacyStr.trim().startsWith('__PLACEHOLDER__');
  const tcc = Array.isArray(s.tissue_contacting_components)
    ? (s.tissue_contacting_components as unknown[])
    : [];
  const tccRows: unknown[][] = tcc
    .filter((c): c is Record<string, unknown> => typeof c === 'object' && c !== null)
    .map((c) => [c.component, c.material]);
  return (
    <NumberedSection prefix="Section" num={num} title="Device Description">
      <Field label="Device trade name / proprietary name:" value={s.trade_name} />
      <Field label="Common or usual name:" value={s.common_name} />
      <Field label="Models / sizes included in this submission:" value={s.models_included} />
      {hasLegacy ? (
        <FieldProse label="Device description:" body={legacy} />
      ) : (
        <>
          <Field label="Accessories included in this submission:" value={s.accessories} />
          <Section6SamdFields s={s} />
          <FieldProse
            label="Performance specifications:"
            body={prose(s.performance_specifications)}
            fallbackHint="device performance specifications"
          />
          <FieldProse
            label="Device design requirements:"
            body={prose(s.design_requirements)}
            fallbackHint="brief device design requirements"
          />
          <FieldGroup label="Tissue-contacting components & materials:">
            <DossierTable headers={['Component', 'Material']} rows={tccRows} />
          </FieldGroup>
          <Field
            label="Diagrams / dimensions / tolerances / schematics:"
            value={s.diagrams_note}
            fallbackHint="reference to diagrams / dimensions / tolerances / schematics"
          />
        </>
      )}
      <h4 className="text-[14px] font-bold text-navy mt-4 mb-2">Assay &amp; Instrument Information</h4>
      <Field label="Is there an instrument associated with the assay(s)?" value={ai.has_instrument} />
      <Field label="Instrument previously FDA-reviewed / under review?" value={ai.instrument_fda_reviewed} />
      <Field label="Instrument modified to run the new assay(s)?" value={ai.instrument_modified_for_assay} />
      <FieldProse label="Assay methodology & instrument/platform:" body={prose(ai.description)} />
      <h4 className="text-[14px] font-bold text-navy mt-4 mb-2">Failure Alerts &amp; Fail-Safe</h4>
      <Gating
        question="Does the device/instrument have failure alerts or fail-safe mechanisms?"
        section={fa}
      />
    </NumberedSection>
  );
}

export function IvdShelfLife({
  dossier,
  sectionKey,
  num,
}: {
  dossier: Record<string, unknown>;
  sectionKey: string;
  num: number;
}) {
  const s = obj(dossier, sectionKey);
  return (
    <NumberedSection prefix="Section" num={num} title="Shelf Life & Stability">
      <Field label="Does the device have a defined shelf life?" value={s.has_shelf_life} />
      <Field label="Shelf life duration:" value={s.shelf_life_duration} />
      <FieldProse label="Shelf life validation:" body={prose(s.shelf_life_validation)} />
      <FieldProse
        label="Reagent / calibrator / control stability:"
        body={prose(s.reagent_stability)}
      />
    </NumberedSection>
  );
}

export function IvdPerformanceBody({ dossier, sectionKey }: { dossier: Record<string, unknown>; sectionKey: string }) {
  const s = obj(dossier, sectionKey);
  const ap = obj(s, 'analytical_performance');
  const cs = obj(s, 'comparison_studies');
  const animal = obj(s, 'animal_testing');
  const clinical = obj(s, 'clinical_testing');
  const clinicalIsYes = String(clinical.gating_response ?? 'No') === 'Yes';
  const studies: [string, string][] = [
    ['Precision / reproducibility', 'precision_reproducibility'],
    ['Linearity / reportable range', 'linearity_reportable_range'],
    ['Analytical sensitivity (LoB/LoD/LoQ)', 'analytical_sensitivity'],
    ['Analytical specificity (interference & cross-reactivity)', 'analytical_specificity'],
    ['Trueness', 'trueness'],
    ['Carryover', 'carryover'],
    ['Specimen stability', 'specimen_stability'],
    ['Specimen / matrix validation', 'specimen_validation'],
  ];
  return (
    <>
      <h4 className="text-[14px] font-bold text-navy mt-2 mb-2">Analytical Performance (CLSI EP-series)</h4>
      {studies.map(([label, key]) => (
        <IvdStudyRow key={key} label={label} study={obj(ap, key)} />
      ))}
      <FieldProse label="Detailed analytical results:" body={prose(ap.detailed_results)} />
      <FieldProse label="Conclusion:" body={prose(ap.conclusion)} />

      <h4 className="text-[14px] font-bold text-navy mt-4 mb-2">Comparison Studies</h4>
      <IvdStudyRow label="Method comparison" study={obj(cs, 'method_comparison')} />
      <IvdStudyRow label="Matrix / specimen comparison" study={obj(cs, 'matrix_comparison')} />

      <h4 className="text-[14px] font-bold text-navy mt-4 mb-2">Reference Range &amp; Accuracy</h4>
      <IvdStudyRow label="Reference range / expected values" study={obj(s, 'reference_range')} />
      <IvdStudyRow label="Accuracy (PPA/NPA or agreement)" study={obj(s, 'accuracy')} />

      <h4 className="text-[14px] font-bold text-navy mt-4 mb-2">Animal Performance Studies</h4>
      <Gating question="Were animal studies conducted to support this submission?" section={animal} />

      <h4 className="text-[14px] font-bold text-navy mt-4 mb-2">Clinical Performance Studies</h4>
      {clinicalIsYes ? (
        <>
          <Field label="Were clinical studies conducted or relied upon?" value="Yes." />
          {prose(clinical.study_summary) != null && (
            <FieldProse label="Clinical study summary:" body={prose(clinical.study_summary)} />
          )}
          {prose(clinical.efficacy_results) != null && (
            <FieldProse label="Efficacy results:" body={prose(clinical.efficacy_results)} />
          )}
          {prose(clinical.safety_analysis) != null && (
            <FieldProse label="Safety analysis:" body={prose(clinical.safety_analysis)} />
          )}
          {prose(clinical.risk_benefit_analysis) != null && (
            <FieldProse label="Risk-benefit analysis:" body={prose(clinical.risk_benefit_analysis)} />
          )}
          {prose(clinical.statistical_analysis) != null && (
            <FieldProse label="Statistical analysis plan:" body={prose(clinical.statistical_analysis)} />
          )}
        </>
      ) : (
        <Gating
          question="Were clinical studies conducted, or is clinical data being relied upon, to support this submission?"
          section={clinical}
        />
      )}
    </>
  );
}

export function IvdPerformance({
  dossier,
  sectionKey,
  num,
}: {
  dossier: Record<string, unknown>;
  sectionKey: string;
  num: number;
}) {
  return (
    <NumberedSection prefix="Section" num={num} title="Performance Testing">
      <IvdPerformanceBody dossier={dossier} sectionKey={sectionKey} />
    </NumberedSection>
  );
}
